import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export interface LinkedAccountProfileData {
  displayName: string | null;
  avatarUrl: string | null;
  handle: string | null;
  providerAccountId: string | null;
  subscriberCount: number | null;
  followerCount: number | null;
}

export interface LinkedAccount {
  id: string;
  provider: string;
  providerId: string;
  providerEmail: string | null;
  status: 'connected' | 'syncing' | 'needs_reconnect' | 'disconnected';
  scopes: string[];
  tokenExpiresAt: string | null;
  lastInsightSyncAt: string | null;
  lastSyncError: string | null;
  connectedAt: string;
  /** 'tenant' = a channel this user hosts for its owner (own tokens); default 'owner'. */
  linkRole?: 'owner' | 'tenant';
  profileData: LinkedAccountProfileData | null;
}

export interface User {
  id: string;
  email: string;
  name: string;
  avatarUrl: string;
  plan: string;
  role: string;
  billingCurrency: string;
  billingCurrencyLocked?: boolean;
  phone?: string | null;
  linkedAccounts: LinkedAccount[];
  stats: { projectsCount: number; shortsCreated: number };
}

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
}

interface AuthContextType extends AuthState {
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  logout: async () => {},
  refreshUser: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
  });

  const fetchUser = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/me`, { credentials: 'include' });
      if (res.ok) {
        const user = await res.json();
        setState({ isAuthenticated: true, isLoading: false, user });
      } else {
        setState({ isAuthenticated: false, isLoading: false, user: null });
      }
    } catch {
      setState({ isAuthenticated: false, isLoading: false, user: null });
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API_BASE}/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch { /* ignore */ }
    setState({ isAuthenticated: false, isLoading: false, user: null });
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, logout, refreshUser: fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
};
